import { Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import {
  useBatchAddItemTagsMutation,
  useBatchRemoveItemTagsMutation,
} from "@/modules/mine";
import { TagSelector } from "@/modules/mine/components/tag-selector";
import type { Item, ItemTagData } from "@/modules/mine/types";
import type { DialogComponentProps } from "@/shared/components/dialog";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";
import { DialogFooter } from "@/shared/ui/dialog";

type BulkTagsResult = {
  action: "updated";
  itemIds: string[];
};

interface BulkTagsProps extends DialogComponentProps<BulkTagsResult> {
  items: Item[];
}

function getUniqueItemTags(items: Item[]) {
  const tagMap = new Map<string, ItemTagData>();

  items.forEach((item) => {
    item.tags?.forEach((tag) => {
      tagMap.set(tag.id, tag);
    });
  });

  return Array.from(tagMap.values());
}

export function BulkAddTags({ close, items, resolve }: BulkTagsProps) {
  const [tags, setTags] = useState<ItemTagData[]>([]);
  const [error, setError] = useState("");
  const mutation = useBatchAddItemTagsMutation();
  const itemIds = useMemo(() => items.map((item) => item.id), [items]);

  const handleSubmit = async () => {
    if (mutation.isPending) {
      return;
    }

    if (tags.length === 0) {
      setError("请选择要添加的标签");
      return;
    }

    if (itemIds.length === 0) {
      return;
    }

    try {
      await mutation.mutateAsync({
        ids: itemIds,
        tag_ids: tags.map((tag) => tag.id),
      });

      toast.success(`已为 ${itemIds.length} 个收藏添加标签`);
      resolve({
        action: "updated",
        itemIds,
      });
    } catch {
      // 全局请求拦截器负责展示接口错误。
    }
  };

  return (
    <div className="space-y-5">
      <div
        className={cn(
          "flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1",
          error && "border-destructive",
        )}
      >
        <TagSelector
          value={tags}
          onChange={(nextTags) => {
            setTags(nextTags);
            if (nextTags.length > 0) {
              setError("");
            }
          }}
          placeholder="搜索或创建标签..."
            triggerClassName="bg-muted px-2 hover:bg-muted/80"
        />
      </div>
      {error ? <p className="-mt-3 text-xs text-destructive">{error}</p> : null}

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={close}
          disabled={mutation.isPending}
        >
          取消
        </Button>
        <Button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? <Loader2 className="animate-spin" /> : null}
          确认
        </Button>
      </DialogFooter>
    </div>
  );
}

export function BulkRemoveTags({ close, items, resolve }: BulkTagsProps) {
  const [tags, setTags] = useState<ItemTagData[]>([]);
  const [error, setError] = useState("");
  const mutation = useBatchRemoveItemTagsMutation();
  const itemIds = useMemo(() => items.map((item) => item.id), [items]);
  const existingTags = useMemo(() => getUniqueItemTags(items), [items]);

  const handleSubmit = async () => {
    if (mutation.isPending) {
      return;
    }

    if (tags.length === 0) {
      setError("请选择要删除的标签");
      return;
    }

    if (itemIds.length === 0) {
      return;
    }

    try {
      await mutation.mutateAsync({
        ids: itemIds,
        tag_ids: tags.map((tag) => tag.id),
      });

      toast.success(`已从 ${itemIds.length} 个收藏移除标签`);
      resolve({
        action: "updated",
        itemIds,
      });
    } catch {
      // 全局请求拦截器负责展示接口错误。
    }
  };

  const handleAddExistingTag = (tag: ItemTagData) => {
    setTags((current) =>
      current.some((selectedTag) => selectedTag.id === tag.id)
        ? current
        : [...current, tag],
    );
    setError("");
  };

  return (
    <div className="space-y-5">
      <div
        className={cn(
          "flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1",
          error && "border-destructive",
        )}
      >
        <TagSelector
          value={tags}
          onChange={(nextTags) => {
            setTags(nextTags);
            if (nextTags.length > 0) {
              setError("");
            }
          }}
          allowCreate={false}
          placeholder="搜索要删除的标签..."
          triggerLabel="选择标签"
            triggerClassName="bg-muted px-2 hover:bg-muted/80"
        />
      </div>
      {error ? <p className="-mt-3 text-xs text-destructive">{error}</p> : null}
      <div className="space-y-2">
        {existingTags.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {existingTags.slice(0, 10).map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => handleAddExistingTag(tag)}
                className="inline-flex h-6 items-center gap-1 rounded-md bg-muted px-2 text-[11px] text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground"
              >
                #{tag.name}
              </button>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            已选择的收藏暂无可删除标签。
          </p>
        )}
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={close}
          disabled={mutation.isPending}
        >
          取消
        </Button>
        <Button
          type="button"
          variant="destructive"
          onClick={() => void handleSubmit()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? <Loader2 className="animate-spin" /> : null}
          确认
        </Button>
      </DialogFooter>
    </div>
  );
}
